import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { meService } from '../services/api';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { phoneTelHref, emailMailtoHref } from '../utils/contactLinks';
import './TenantAccount.css';

function isEmpty(val?: string | null): boolean {
  return val == null || String(val).trim() === '';
}

function Detail({ label, value }: { label: string; value?: React.ReactNode }) {
  const empty = value == null || (typeof value === 'string' && isEmpty(value));
  return (
    <div className="ta-detail">
      <span className="ta-detail__label">{label}</span>
      <span className="ta-detail__value">{empty ? '—' : value}</span>
    </div>
  );
}

const TenantHostel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [hostel, setHostel] = useState<Awaited<ReturnType<typeof meService.getAccount>>['hostel'] | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await meService.getAccount();
      setHostel(data.hostel);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to load hostel details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <div className="loading-container">Loading hostel…</div>;
  }

  if (!hostel) {
    return (
      <div className="ta-page">
        <p className="ta-empty">Unable to load hostel details.</p>
      </div>
    );
  }

  const logoUrl = resolveMediaUrl(hostel.logo);

  return (
    <div className="ta-page">
      <header className="ta-header">
        <div>
          <h1>Hostel</h1>
          <p className="ta-header__sub">Property details and rules for your stay</p>
        </div>
      </header>

      <section className="ta-panel">
        <div className="ta-hostel-hero">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="ta-hostel-logo" />
          ) : (
            <span className="ta-hostel-logo ta-hostel-logo--ph">{hostel.name.charAt(0)}</span>
          )}
          <div>
            <h2>{hostel.name}</h2>
            {hostel.description ? <p className="ta-desc">{hostel.description}</p> : null}
          </div>
        </div>

        <div className="ta-card">
          <h3>Contact &amp; location</h3>
          <div className="ta-grid">
            <Detail label="Address" value={hostel.address} />
            <Detail
              label="City"
              value={[hostel.city, hostel.state, hostel.pincode].filter(Boolean).join(', ') || undefined}
            />
            <Detail
              label="Phone"
              value={
                hostel.phone ? (
                  <a href={phoneTelHref(hostel.phone) || undefined} className="ta-link">
                    {hostel.phone}
                  </a>
                ) : null
              }
            />
            <Detail
              label="Email"
              value={
                hostel.email ? (
                  <a href={emailMailtoHref(hostel.email) || undefined} className="ta-link">
                    {hostel.email}
                  </a>
                ) : null
              }
            />
            {hostel.websiteUrl ? (
              <div className="ta-detail">
                <span className="ta-detail__label">Website</span>
                <a href={hostel.websiteUrl} target="_blank" rel="noopener noreferrer" className="ta-link">
                  {hostel.websiteUrl}
                </a>
              </div>
            ) : null}
          </div>
        </div>

        <div className="ta-card">
          <h3>Hostel rules</h3>
          {isEmpty(hostel.hostelRules) ? (
            <p className="ta-muted">No rules published yet.</p>
          ) : (
            <pre className="ta-rules">{hostel.hostelRules}</pre>
          )}
        </div>
      </section>
    </div>
  );
};

export default TenantHostel;
